import {
  Button,
  Card,
  Table,
  TableBody,
  TableContainer,
  Typography,
  useTheme,
} from '@mui/material';
import { MemoizedScrollbar, TableHeadBasic } from 'src/components';
import { AdvancedTagMappingTableRow } from './AdvancedTagMappingTableRow';
import { ModalTableProps, ModalTableRowData } from './types';
import { useEffect, useState } from 'react';
import { useAuthentication } from 'src/features/authentication/context';
import { AccessRoles } from 'src/features/user-account-details/types';
import { PlusFillIcon } from 'src/assets/icons/PlusFillIcon';

type AdvancedTagMappingTableProps = ModalTableProps & {
  rowData: ModalTableRowData[];
  addNewRow: (times: number) => void;
  dataType: string;
  tagType: string;
  controllerType: string | null;
};

export const AdvancedTagMappingTable = ({
  rowData,
  addNewRow,
  dataType,
  tagType,
  handleRemoveRow,
  errors,
  customErrors,
  areTableValuesUnique,
  setValue,
  values,
  clearErrors,
  controllerType,
}: AdvancedTagMappingTableProps) => {
  const isSetpointOrOutput =
    tagType.toLowerCase() === 'setpoint' || tagType.toLowerCase() === 'output';
  const [ADVANCED_TAG_MAPPING_TABLE_HEAD, SET_ADVANCED_TAG_MAPPING_TABLE_HEAD] = useState([
    { id: 'value', label: 'Value*', align: 'left', testId: 'value-header' },
    {
      id: 'category',
      label: `${tagType} Category`,
      align: 'left',
      testId: 'category-header',
    },
    {
      id: 'name',
      label: `${isSetpointOrOutput ? '' : tagType + ' '}Name${isSetpointOrOutput ? '' : '*'}`,
      align: 'left',
      testId: 'name-header',
    },
  ]);

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
    getCurrentRole,
  } = useAuthentication();
  const theme = useTheme();
  const hasReadAccess =
    getCurrentRole(customerId, siteId) === AccessRoles.SITE_MANAGER ||
    getCurrentRole(customerId, siteId) === AccessRoles.SITE_EMPLOYEE;

  useEffect(() => {
    if (dataType !== 'BOOLEAN' && rowData.length > 1) {
      SET_ADVANCED_TAG_MAPPING_TABLE_HEAD((prevValue) => {
        let tempData = [];

        if (prevValue.find((value) => value.id === 'remove'))
          return tagType.toLowerCase() === 'setpoint' || tagType.toLowerCase() === 'output'
            ? prevValue.filter((value) => value.id !== 'category')
            : prevValue;

        prevValue.forEach((value) => {
          tempData.push(value);
        });

        tempData.push({
          id: 'remove',
          label: '',
          align: 'left',
          testId: 'remove-header',
        });

        return tagType.toLowerCase() === 'setpoint' || tagType.toLowerCase() === 'output'
          ? tempData.filter((value) => value.id !== 'category')
          : tempData;
      });
    } else if (rowData.length <= 1 || dataType === 'BOOLEAN') {
      SET_ADVANCED_TAG_MAPPING_TABLE_HEAD((prevValue) => {
        let tempData = prevValue.filter((value) => value.id !== 'remove');

        return tagType.toLowerCase() === 'setpoint' || tagType.toLowerCase() === 'output'
          ? tempData.filter((value) => value.id !== 'category')
          : tempData;
      });
    }
  }, [rowData, dataType, tagType]);

  return (
    <Card>
      <TableContainer
        sx={{
          position: 'relative',
          overflow: 'unset',
          pb: values.dataType === 'BOOLEAN' ? '16px' : '0px',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <MemoizedScrollbar>
          <Table
            size={'medium'}
            data-sm="add-tag-form"
            sx={{ backgroundColor: theme.palette.background.default }}
          >
            <TableHeadBasic
              headLabel={ADVANCED_TAG_MAPPING_TABLE_HEAD}
              tooltipInfo={{
                id: 'value',
                message: 'Entered values dictate input options in the Controller widget',
              }}
            />
            <TableBody data-sm="table-content" sx={{ pb: '64px' }}>
              {rowData.map((row, i) => (
                <AdvancedTagMappingTableRow
                  key={row?.index?.toString() ?? i.toString()}
                  rowData={rowData}
                  index={row.index}
                  errors={errors}
                  customErrors={customErrors}
                  handleRemoveRow={handleRemoveRow}
                  areTableValuesUnique={areTableValuesUnique}
                  setValue={setValue}
                  values={values}
                  clearErrors={clearErrors}
                  tagType={tagType}
                  controllerType={controllerType}
                />
              ))}
            </TableBody>
          </Table>

          {dataType !== 'BOOLEAN' && !hasReadAccess ? (
            <Button
              sx={{ margin: '32px 16px' }}
              variant="text"
              data-sm="add-tag-to-be-forwarded-button"
              onClick={() => addNewRow(1)}
            >
              <PlusFillIcon width={22} height={22} />
              <Typography variant="button" sx={{ marginLeft: '5px' }}>
                Add
              </Typography>
            </Button>
          ) : null}
        </MemoizedScrollbar>
      </TableContainer>
    </Card>
  );
};
